import { useState, useEffect, useRef } from "react";
import { Button } from "@component/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@component/card";
import {
  ArrowRightIcon,
  CheckIcon,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { ProgressBar, ProgressStep } from "@component/progress-bar";
import { getMyCV } from "@service/cv";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { DailyCall } from "@daily-co/daily-js";
import { CVData } from "@type/cv";
import { getFirstName } from "@util/name";
import {
  getInterviewStatus,
  triggerInterviewDoneEmail,
  updateInterviewStatus,
} from "@service/interview";
import { CustomInterviewSection, VideoInterviewRef } from "@type/videoInterview"; // Add this import
// Legacy voice/Vietnamese interview containers are intentionally not
// imported here — AiInterview now renders the single V2 component for
// every code path (screening + assessment + practice + vietnamese).
import { getCurrentUser } from "@service/user";
import { catchErrorLog } from "@util/sentry";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import { formatCV } from "@util/string";
import BasicLayout from "@container/layout/basic";
import CustomVoiceInterviewV2 from "@container/custom-voice-interview-v2";
import Snackbar from "@component/snackbar";
import { InterviewAssessment } from "@type/assessments";
import { get_agent_name } from "@util/ai-interview";
// Vietnamese variant is now handled by passing `interviewLanguage`
// to the single CustomVoiceInterviewV2 component.
import { getAssessment } from "@service/assessment";
import { useLocation } from 'react-router-dom';
import { CompanyPreferences } from "@type/company";
import { trackEvent } from "@util/mixpanel";
import InterviewShortCard from "@container/interview-short-card";
import {
  interviewStatusAborted,
  interviewStatusInComplete,
  interviewStatusPartial,
} from "@constant/app";

export default function AiInterview({
  isShowBrand = false,
  companyPreferences = undefined,
  routeTo = "/dashboard",
  interviewType = "voice",
  showLayout = true,
  handleEnd,
  handleStarted,
  isSkipInterview = false,
  isLoadingSkipInterview = false,
  onSkipInterview = () => { },
  onInterviewStatusChange = (status: string) => { },
  showButtonAIInternship = false,
  handleEndSession,
  isApplyFlow = false,
  customConfig = null,
}: {
  handleEndSession?: () => void,
  isApplyFlow?: boolean,
  isShowBrand?: boolean,
  companyPreferences?: CompanyPreferences | undefined,
  showButtonAIInternship?: boolean,
  routeTo: string,
  interviewType: "voice" | "video",
  showLayout?: boolean,
  handleStarted?: (deviceInfo: string) => void,
  handleEnd?: () => void,
  isSkipInterview?: boolean,
  isLoadingSkipInterview?: boolean,
  onSkipInterview?: () => void,
  customConfig?: InterviewAssessment | null,
  onInterviewStatusChange?: (status: string) => void
}) {

  const { interviewid, jobid } = useParams() || null;

  const navigate = useNavigate();
  const [interviewStage, setInterviewStage] = useState<string>("interview");
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [cvData, setCVData] = useState<CVData | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [interviewStatus, setInterviewStatus] = useState<string>("");
  const [agentName, setAgentName] = useState<string>("");
  // Add a ref to the VideoInterview component
  const videoInterviewRef = useRef<VideoInterviewRef>(null);
  // Add state for browser compatibility

  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);


  const API_BASE_URL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
  const PIPECAT_API_URL = import.meta.env.VITE_PIPECAT_API_URL;
  const PIPECAT_AUTH_TOKEN = import.meta.env.VITE_PIPECAT_AUTH_TOKEN;

  // Single replica configuration
  const REPLICA_CONFIG = {
    label: import.meta.env.VITE_TAVUS_REPLICA_LABEL,
    replicaId: import.meta.env.VITE_TAVUS_REPLICA_ID,
    personaId: import.meta.env.VITE_TAVUS_PERSONA_ID,
    greeting: import.meta.env.VITE_TAVUS_GREETING,
  };

  const AWS_ASSUME_ROLE_ARN = import.meta.env.VITE_AWS_ASSUME_ROLE_ARN;
  const RECORDING_S3_BUCKET_REGION = import.meta.env.VITE_AWS_S3_BUCKET_REGION;
  const RECORDING_S3_BUCKET_NAME = import.meta.env.VITE_AWS_S3_BUCKET_NAME;

  const [assessment, setAssessment] = useState<InterviewAssessment | null>(null);
  const [formattedSections, setFormattedSections] = useState<CustomInterviewSection[]>([]);
  const [isLoadingInterviewDetails, setIsLoadingInterviewDetails] = useState(true);
  const [, setError] = useState<string | null>(null);

  const location = useLocation();
  const isPracticeInterview = location.state?.isPracticeInterview || false; // Check if this is a practice interview

  useEffect(() => {
    if (jobid) {
      trackEvent('ai_interview_waiting_room', {
        job_id: jobid || "",
      })
    }
  }, [jobid])

  // fetch cv data from service when component mounts
  useEffect(() => {
    setIsLoading(true);
    getCurrentUser().then((res) => {
      if (!res) return;
      setUserId(res.data?.data.id as string || "");

      if (interviewid) {
        setAgentName(import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || "my-custom-staging-smart-turn");
      }
      else {
        setAgentName(get_agent_name(res.data?.data.referral_code as string));
      }
    })
    getMyCV()
      .then((res) => {
        if (res.code === 200 && res.data) {
          setCVData(res.data.CV_data);
        } else {
          catchErrorLog(`Error fetching CV data: ${res.message}`);
        }
      })
      .catch((err) => {
        catchErrorLog(`Error fetching CV data: ${err}`);
      })

    getInterviewStatus(interviewid).then((res) => {
      console.log("Interview Status Check Response:", res);
      setInterviewStatus(res?.data?.status || null);
    }
    ).then(() => {
      setIsLoading(false);
    })

    const fetchAssessmentData = async () => {
      if (!interviewid) {
        setIsLoadingInterviewDetails(false);
        setError("Interview ID is missing");
        return;
      }

      setIsLoadingInterviewDetails(true);
      setError(null);

      try {
        const response = await getAssessment(interviewid as string);
        if (response) {
          console.log("Assessment response:", response);
          const assessmentData = response as InterviewAssessment;
          setAssessment(assessmentData);
          // Format sections for the test interview
          const sections = assessmentData?.content.sections.map((section: any) => ({
            title: section.title,
            instruction: section.instruction,
            duration: section.duration,
            questions: section.questions?.map((q: any) => q.text).filter((text: string) => text?.trim() !== "") || undefined,
            transition: section.transition || undefined
          }));
          console.log("Formatted Sections:", sections);
          setFormattedSections(sections as CustomInterviewSection[]);
        } else {
          setError(response || "Failed to load your screening");
        }
      } catch (err) {
        console.error("Error fetching assessment:", err);
        setError("An unexpected error occurred");
      } finally {
        setIsLoadingInterviewDetails(false);
      }
    }

    fetchAssessmentData();
  }, [interviewid]);

  const onReload = () => {
    setIsLoading(true);
    setInterviewStage("interview");
    setInterviewStatus("");

    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  }

  const steps: ProgressStep[] = [
    {
      label: "AI Interview",
      isCompleted: currentStep > 1,
      isActive: currentStep === 1,
    },
    {
      label: "Complete",
      isCompleted: currentStep > 2,
      isActive: currentStep === 2,
    },
  ]

  // Get candidate name for conversation name
  const getCandidateName = () => {
    if (!cvData) return "Anonymous Candidate";
    const cv = cvData
    return cv?.name || "Anonymous Candidate";
  };

  // Interview configuration using CV data
  const INTERVIEW_CONFIG = {
    conversationName: `${getCandidateName()}: Screening Interview (${new Date().toISOString()})`,
    conversationalContext: `Never ask the user about a 'position', this is a general screening interview. \n\nCandidate CV/Resume:\n${formatCV(cvData)}`,
  };

  useEffect(() => {
    // Log CV data when it's available
    if (cvData) {
      setIsLoading(false);
    }
  }, [cvData]);

  // Handle completion of interview and cleanup
  const handleInterviewComplete = () => {
    setIsSnackbarOpen(true);
    // End the interview if it's still active
    if (videoInterviewRef.current) {
      console.log("Ending interview and cleaning up media resources");
      videoInterviewRef.current.endInterview('interview_completed');
    }

    // Update state to show completion UI for regular interviews
    setInterviewStage("complete");
    setCurrentStep(2);
    if (handleEnd) {
      if (!isPracticeInterview && !interviewid) {
        // screening interview
        console.log("Updating screening interview status to complete");
        updateInterviewStatus({ new_status: "Complete" }).then(() => { console.log("Interview status updated to complete") })
        handleEnd()
      }
      else {
        handleEnd();
      }
    }
  };

  const onCompletedInterview = () => {
    if (!isPracticeInterview && !interviewid) {
      console.log("Triggering interview done email for screening interview");
      triggerInterviewDoneEmail().then(() => {
        console.log("Interview done email triggered successfully");
      }).catch((error) => {
        catchErrorLog(`failed to trigger interview done email, err: ${error}`);
      })
    }
  }

  // Clean up any media when showing the completed screen directly due to status
  useEffect(() => {
    if (interviewStatus === 'Complete' && interviewStage === "interview" && !isPracticeInterview) {
      console.log("Interview already completed - cleaning up resources");
      // Set UI to completed state without showing the interview
      setInterviewStage("complete")
      setCurrentStep(2);

      if (handleEnd) {
        updateInterviewStatus({ new_status: "Complete" }).then(() => { console.log("Interview status updated to complete") })
        handleEnd()
      }
    }
  }, [interviewStatus, interviewStage, isPracticeInterview]);

  const handleSnackbarClose = () => {
    setIsSnackbarOpen(false);
  };

  const callObject = useCallObject({}) as DailyCall;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (showLayout) {
    return (
      <>
        <BasicLayout disableMinHeight showFooter={false}>
          <div className="container max-w-6xl mx-auto px-4 py-6 sm:py-10 flex justify-center items-start sm:items-center min-h-[calc(100vh-80px)]">
              {[interviewStatusAborted, interviewStatusPartial].includes(interviewStatus) ? (
                <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-xl">
                  <InterviewShortCard
                    isApplyFlow={isApplyFlow}
                    onRetakeInterview={() => {
                      onReload();
                    }}
                    onViewPartialInsights={() => {
                      // When the candidate is in an assessment-driven
                      // flow we have the specific interview id, so route
                      // straight to its detail page instead of the
                      // generic /interview/assessment landing.
                      window.location.href = interviewid
                        ? `/interview/${interviewid}`
                        : '/interview/assessment';
                    }}
                    description={(
                      <>
                        We didn’t get enough answers to generate your full results.
                        <br /><br />
                        {isApplyFlow ? (
                          <>Retake the interview to complete your application and improve your chances for this role.</>
                        ) : (
                          <>You can retake the interview or view the<br />partial insights we captured.</>
                        )}
                      </>
                    )}
                  />
                </div>
              ) : (
                <>
                  {
                      interviewStage === "interview" && (interviewStatus !== 'Complete' || isPracticeInterview) && !isLoading ? (
                        <DailyProvider callObject={callObject}>
                          <SpeechDetectionProvider
                            volumeThreshold={30}
                            silenceDelay={400}
                            autoStart={true}
                          >
                            {isLoadingInterviewDetails ? null : (
                              <CustomVoiceInterviewV2
                                interviewId={(interviewid as string) || ""}
                                pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || PIPECAT_API_URL}
                                agentName={
                                  interviewid
                                    ? import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || agentName
                                    : agentName
                                }
                                authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || PIPECAT_AUTH_TOKEN}
                                s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || RECORDING_S3_BUCKET_NAME}
                                s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || RECORDING_S3_BUCKET_REGION}
                                awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || AWS_ASSUME_ROLE_ARN}
                              fastapiURL={API_BASE_URL || ""}
                              userId={userId}
                              userCV={formatCV(cvData)}
                              userName={getFirstName(cvData?.name as string) || "Test User"}
                                interviewName={assessment?.content.interviewName || INTERVIEW_CONFIG.conversationName}
                                description={assessment?.content.description || "This is a screening interview."}
                              maxDuration={assessment?.content.maxDuration || 20}
                              interviewContext={assessment?.content.interviewContext || INTERVIEW_CONFIG.conversationalContext}
                              closingStatement={assessment?.content.closingStatement || ""}
                              interviewerPersona={assessment?.content.interviewerPersona || ""}
                              toneAndCommunication={assessment?.content.toneAndCommunication || undefined}
                              interviewFlowPrinciples={assessment?.content.interviewFlowPrinciples || undefined}
                              informationToShare={assessment?.content.informationToShare || undefined}
                              customGreeting={assessment?.content.customGreeting || REPLICA_CONFIG.greeting}
                              aiName={assessment?.content.aiName || REPLICA_CONFIG.label}
                                sections={(formattedSections as CustomInterviewSection[]) || []}
                              onCloseModal={handleInterviewComplete}
                              onCompletedInterview={onCompletedInterview}
                                isPracticeInterview={isPracticeInterview}
                                isSkipInterview={isSkipInterview}
                                isLoadingSkipInterview={isLoadingSkipInterview}
                                onSkipInterview={onSkipInterview}
                                interviewLanguage={
                                  assessment?.content.interviewLanguage
                                  || ((agentName === "my-staging-vietnamese-agent" || agentName === "my-vietnamese-agent")
                                    ? "Vietnamese"
                                    : "en-US")
                                }
                                voice={assessment?.content.voice || "male"}
                                handleEndSession={handleEndSession}
                                onReload={onReload}
                                onInterviewStatusChange={(status: string) => {
                                  setInterviewStatus(status);
                                  if (onInterviewStatusChange) {
                                    onInterviewStatusChange(status);
                                  }
                                }}
                              />
                            )}
                          </SpeechDetectionProvider>
                        </DailyProvider>
                    ) : null
                  }
                </>
              )}

              {(interviewStage === "complete" || (interviewStatus === 'Complete' && !isPracticeInterview)) && (
                isPracticeInterview ?
                  <>
                    <Card>
                      <CardHeader>
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                        <CardTitle className="text-center text-2xl">
                          Practice Interview Complete!
                        </CardTitle>
                        <CardDescription className="text-center">
                          Thank you for completing your {assessment?.content.interviewName} practice interview with <strong>ourteam AI.</strong>
                          <br />
                          Your insights are now being generated and will be available in 5-10 minutes.
                        </CardDescription>
                      </CardHeader>
                      <CardFooter>
                        <Button
                          onClick={() => { navigate(`/interview`) }}
                          className="w-full group"
                        >
                          View Your Interviews
                          <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardFooter>
                    </Card>
                    <Snackbar
                      message="Thank you for your feedback!"
                      description="Your input helps us improve our interview process."
                      isOpen={isSnackbarOpen}
                      onClose={handleSnackbarClose}
                      variant="success"
                    />
                  </> :
                  <>
                    <Card>
                      <CardHeader>
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                        <CardTitle className="text-center text-2xl">
                          Interview Complete!
                        </CardTitle>
                        <CardDescription className="text-center">
                          Thank you for completing your {assessment?.content.maxDuration || 20} minutes screening interview with <strong>ourteam AI.</strong>
                          <br />
                          We’ll now share your profile with top companies hiring for roles that match your skills.
                        </CardDescription>
                      </CardHeader>
                      <CardFooter>
                        <Button
                          onClick={() => { console.log("navigating to:", routeTo); navigate(routeTo || "/dashboard") }}
                          className="w-full group"
                        >
                          Continue to Dashboard
                          <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardFooter>
                    </Card>
                    <Snackbar
                      message="Thank you for your feedback!"
                      description="Your input helps us improve our interview process."
                      isOpen={isSnackbarOpen}
                      onClose={handleSnackbarClose}
                      variant="success"
                    />
                  </>
              )}

            </div>
            {/* OUTSIDE the container above: that div is `flex` (row) with
                `sm:items-center`, so a child here became a column beside the
                interview panel, vertically centered — the strip rendered
                floating to its right instead of under the page. As a sibling it
                flows below, which is where the other branch of this component
                already puts it (same className). */}
          {/* <SocialProofIndicator variant="companies" className="opacity-80 mt-10 mb-20 sm:mb-0" /> */}
        </BasicLayout>
        {
          interviewStage === "interview" && interviewStatus !== 'Complete' ?
            null :
            <div className="w-full fixed bottom-0 left-0 right-0 px-4 sm:px-8 pt-8 pb-8 sm:pb-4 bg-white shadow-sm">
              <ProgressBar
                currentStep={currentStep}
                steps={steps}
                data-pol-id="rhbyfv"
                data-pol-file-name="employer-onboarding"
                data-pol-file-type="page"
              />
            </div>
        }
      </>
    );
  }
  else {
    return (
      <>
        <div className="container max-w-6xl mx-auto px-4 py-6 sm:py-10 flex justify-center items-start sm:items-center min-h-[calc(100vh-80px)]">
          {[interviewStatusAborted, interviewStatusPartial, interviewStatusInComplete].includes(interviewStatus) ? (
            <div className="container mx-auto px-0 sm:px-4 pt-0 pb-2 sm:py-8 max-w-xl">
              <InterviewShortCard
                isApplyFlow={isApplyFlow}
                onRetakeInterview={() => {
                  onReload();
                }}
                onViewPartialInsights={() => {
                  window.location.href = interviewid
                    ? `/interview/${interviewid}`
                    : '/interview/assessment';
                }}
                description={(
                  <>
                    We didn’t get enough answers to generate your full results.
                    <br /><br />
                    {isApplyFlow ? (
                      <>Retake the interview to complete your application and improve your chances for this role.</>
                    ) : (
                      <>You can retake the interview or view the<br />partial insights we captured.</>
                    )}
                  </>
                )}
              />
            </div>
          ) : (
            <>
              {
                  interviewStage === "interview" && (interviewStatus !== 'Complete' || isPracticeInterview) && !isLoading ? (
                    <DailyProvider callObject={callObject}>
                      <SpeechDetectionProvider
                        volumeThreshold={30}
                        silenceDelay={400}
                        autoStart={true}
                      >
                        {isLoadingInterviewDetails ? null : (
                          <CustomVoiceInterviewV2
                            interviewId={(interviewid as string) || ""}
                            pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || PIPECAT_API_URL}
                            agentName={
                              interviewid
                                ? import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || agentName
                                : agentName
                            }
                            authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || PIPECAT_AUTH_TOKEN}
                            s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || RECORDING_S3_BUCKET_NAME}
                            s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || RECORDING_S3_BUCKET_REGION}
                            awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || AWS_ASSUME_ROLE_ARN}
                          fastapiURL={API_BASE_URL || ""}
                          userId={userId}
                          userCV={formatCV(cvData)}
                          userName={getFirstName(cvData?.name as string) || "Test User"}
                            interviewName={assessment?.content.interviewName || INTERVIEW_CONFIG.conversationName}
                            description={assessment?.content.description || "This is a screening interview."}
                          maxDuration={assessment?.content.maxDuration || 20}
                          interviewContext={assessment?.content.interviewContext || INTERVIEW_CONFIG.conversationalContext}
                          closingStatement={assessment?.content.closingStatement || ""}
                          interviewerPersona={assessment?.content.interviewerPersona || ""}
                          toneAndCommunication={assessment?.content.toneAndCommunication || undefined}
                          interviewFlowPrinciples={assessment?.content.interviewFlowPrinciples || undefined}
                          informationToShare={assessment?.content.informationToShare || undefined}
                          customGreeting={assessment?.content.customGreeting || REPLICA_CONFIG.greeting}
                          aiName={assessment?.content.aiName || REPLICA_CONFIG.label}
                            sections={(formattedSections as CustomInterviewSection[]) || []}
                          onCloseModal={handleInterviewComplete}
                          onCompletedInterview={onCompletedInterview}
                            isPracticeInterview={isPracticeInterview}
                            isSkipInterview={isSkipInterview}
                            isLoadingSkipInterview={isLoadingSkipInterview}
                            onSkipInterview={onSkipInterview}
                            interviewLanguage={
                              assessment?.content.interviewLanguage
                              || ((agentName === "my-staging-vietnamese-agent" || agentName === "my-vietnamese-agent")
                                ? "Vietnamese"
                                : "en-US")
                            }
                            voice={assessment?.content.voice || "male"}
                            handleEndSession={handleEndSession}
                            onReload={onReload}
                            onInterviewStatusChange={(status: string) => {
                              setInterviewStatus(status);
                              if (onInterviewStatusChange) {
                                onInterviewStatusChange(status);
                              }
                            }}
                          />
                        )}
                      </SpeechDetectionProvider>
                    </DailyProvider>
                ) : null
              }
            </>
          )}
        </div>
        {
          interviewStage === "interview" && interviewStatus !== 'Complete' ?
            null :
            showLayout ? (
              <div className="w-full fixed bottom-0 left-0 right-0 px-4 sm:px-8 pt-8 pb-8 sm:pb-4 bg-white shadow-sm">
                <ProgressBar
                  currentStep={currentStep}
                  steps={steps}
                />
              </div>
            ) : null}

        {/* {![interviewStatusAborted, interviewStatusPartial].includes(interviewStatus) && (
          <SocialProofIndicator variant="companies" className="opacity-80 mt-10 mb-20 sm:mb-0" />
        )} */}
      </>
    )
  }
}